import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Form, Radio, Result, Row, Typography, Upload } from "antd";
import axios from "axios";
import { message } from "components/Messages/Messages";
import { uploadButton } from "components/UploadButton/UploadButton";
import Calendar from "containers/Company/EventBooking/Booking/Calendar";
import { BookingSeason } from "containers/Company/EventBooking/interface";
import { getBase64 } from "helpers/functions";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

const { Title } = Typography;

const CreateOrEditBookingSeason: React.FC = () => {
  const [form] = Form.useForm();
  const [is_fall, setIsFall] = useState<boolean>();
  const [preview, setPreview] = useState<BookingSeason>();
  const [fetching, setFetching] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [season, setSeason] = useState<BookingSeason>();
  const { id } = useParams<{ id: string }>();
  const [priceModelImage, setPriceModelImage] = useState<File | null>(null);
  const [priceModelImagePreview, setPriceModelImagePreview] = useState<string | null>(null);
  const [removePriceModel, setRemovePriceModel] = useState<boolean>(false);

  const history = useHistory();

  useEffect(() => {
    if (!id) return;
    setFetching(true);
    axios
      .get<BookingSeason>(`/booking-seasons/${id}/`)
      .then(response => {
        setSeason(response.data);
        setPriceModelImage(null);
        setPriceModelImagePreview(null);
        setRemovePriceModel(false);
      })
      .finally(() => setFetching(false));
  }, [id]);

  const update_season = (formData: FormData) => {
    axios
      .put(`/booking-seasons/${season?.id}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        message("success", "Bookingsesongen ble oppdatert");
        history.push(`/admin/tools/booking-seasons/`);
      })
      .catch(e => {
        if (
          "response" in e &&
          "data" in e.response &&
          typeof e.response.data === "object" &&
          "error" in e.response.data
        ) {
          message("error", e.response.data.error);
        } else {
          message("error", "Kunne ikke opprette bookingsesongen");
        }
      })
      .finally(() => setLoading(false));
  };

  const create_season = (formData: FormData) => {
    axios
      .post("/booking-seasons/create_season/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        message("success", "Bookingsesongen ble opprettet");
        history.push(`/admin/tools/booking-seasons/`);
      })
      .catch(e => {
        if (
          "response" in e &&
          "data" in e.response &&
          typeof e.response.data === "object" &&
          "error" in e.response.data
        ) {
          message("error", e.response.data.error);
        } else {
          message("error", "Kunne ikke opprette bookingsesongen");
        }
      })
      .finally(() => setLoading(false));
  };

  const onFinish = () => {
    if (!preview) return;
    setLoading(true);

    const formData = new FormData();
    Object.entries(preview).forEach(([key, value]) => {
      if (!value) return;
      if (value instanceof Array) {
        value.forEach(v => formData.append(key, String(v)));
        return;
      }
      formData.append(key, String(value));
    });

    formData.append("updated_at", new Date().toISOString());
    if (!season) {
      formData.append("created_at", new Date().toISOString());
    }

    if (priceModelImage) {
      formData.append("price_model", priceModelImage);
    }
    if (removePriceModel) {
      formData.append("price_model", "");
    }

    if (season) {
      update_season(formData);
    } else {
      create_season(formData);
    }
  };

  useEffect(() => {
    if (!season) return;
    setIsFall(season.is_fall);
    form.setFieldsValue({
      is_fall: season.is_fall,
      year: moment(season.year, "YYYY"),
      booking_open: moment(season.booking_open),
      booking_end: moment(season.booking_end),
      semester_start: moment(season.semester_start),
      semester_end: moment(season.semester_end),
      unavailable_dates_collaboration_partner:
        season.unavailable_dates_collaboration_partner && season.unavailable_dates_collaboration_partner?.length > 0
          ? season.unavailable_dates_collaboration_partner.map(date => moment(date))
          : null,
      unavailable_dates_other:
        season.unavailable_dates_other && season.unavailable_dates_other?.length > 0
          ? season.unavailable_dates_other.map(date => moment(date))
          : null,
      unavailable_date_ot_start: season.unavailable_date_ot_start ? moment(season.unavailable_date_ot_start) : null,
      unavailable_date_ot_end: season.unavailable_date_ot_end ? moment(season.unavailable_date_ot_end) : null,
      unavailable_date_kd_start: season.unavailable_date_kd_start ? moment(season.unavailable_date_kd_start) : null,
      unavailable_date_kd_end: season.unavailable_date_kd_end ? moment(season.unavailable_date_kd_end) : null,
      price_model: season.price_model ? season.price_model : null,
    });
  }, [season, form]);

  const handlePreview = () => {
    if (preview) return setPreview(undefined);

    form.validateFields().then(() => {
      const formData = form.getFieldsValue();
      const unavailableDatesCollaborationPartner =
        formData.unavailable_dates_collaboration_partner && formData.unavailable_dates_collaboration_partner.length > 0
          ? formData.unavailable_dates_collaboration_partner.map((date: moment.Moment) => date.format("YYYY-MM-DD"))
          : [];
      const unavailableDatesOther =
        formData.unavailable_dates_other && formData.unavailable_dates_other.length > 0
          ? formData.unavailable_dates_other.map((date: moment.Moment) => date.format("YYYY-MM-DD"))
          : [];

      const previewData = {
        ...formData,
        is_fall,
        year: formData.year.format("YYYY"),
        booking_open: formData.booking_open.format("YYYY-MM-DD HH:mm"),
        booking_end: formData.booking_end.format("YYYY-MM-DD HH:mm"),
        semester_start: formData.semester_start.format("YYYY-MM-DD"),
        semester_end: formData.semester_end.format("YYYY-MM-DD"),
        unavailable_dates_collaboration_partner: unavailableDatesCollaborationPartner,
        unavailable_dates_other: unavailableDatesOther,
        unavailable_date_ot_start: formData.unavailable_date_ot_start?.format("YYYY-MM-DD") || null,
        unavailable_date_ot_end: formData.unavailable_date_ot_end?.format("YYYY-MM-DD") || null,
        unavailable_date_kd_start: formData.unavailable_date_kd_start?.format("YYYY-MM-DD") || null,
        unavailable_date_kd_end: formData.unavailable_date_kd_end?.format("YYYY-MM-DD") || null,
        booked_days: season?.booked_days || [],
      };
      setPreview(previewData);
    });
  };

  useEffect(() => {
    if (priceModelImage) {
      getBase64(priceModelImage)
        .then(url => setPriceModelImagePreview(url))
        .catch(() => setPriceModelImagePreview(null));
    }
  }, [priceModelImage]);

  if (id && !season && !fetching)
    return (
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Result title="Fant ikke bookingsesong" subTitle="Dobbeltsjekk URL eller prøv på nytt" status="404" />
      </div>
    );

  return (
    <Card style={{ width: "100%" }}>
      <Title level={2}>{id ? "Rediger bookingsesong" : "Opprett bookingsesong"}</Title>
      <Form form={form} disabled={!!preview || fetching} layout="vertical" onFinish={onFinish}>
        <Row>
          <Col span={24}>
            <Title level={5}>Kalenderår og semester for bookingsesong</Title>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="År"
              name="year"
              tooltip="Angi hvilket kalenderår bedriftspresentasjonene skal arrangeres."
              rules={[{ required: true, message: "Vennligst velg år" }]}
            >
              <DatePicker picker="year" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="Semester"
              name="is_fall"
              tooltip="Angi hvilket semester bedriftspresentasjonene skal arrangeres."
              rules={[{ required: true, message: "Vennligst velg semester" }]}
            >
              <Radio.Group onChange={e => setIsFall(e.target.value)}>
                <Radio value>Høst</Radio>
                <Radio value={false}>Vår</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Title level={5}>Bookingperiode</Title>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="Booking åpner"
              name="booking_open"
              tooltip="Angi dato og klokkeslett for når bookingen åpner for bedrifter. Dette er tidspunktet når bedriften får mulighet til å booke en dag, ikke dagen de skal ha presentasjonen."
              rules={[
                { required: true, message: "Vennligst velg dato og klokkeslett for når bookingen åpner" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const semesterStart = getFieldValue("semester_start");
                    if (semesterStart && value && value.isAfter(semesterStart)) {
                      return Promise.reject(new Error("Datoen må være før sesongstart"));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker
                showTime={{ defaultValue: moment("12:00:00", "HH:mm:ss"), format: "HH:mm" }}
                showSecond={false}
                showNow={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="Booking stenger"
              name="booking_end"
              tooltip="Angi dato og klokkeslett for når bookingen stenger for bedrifter. Dette er tidspunktet når bedriften ikke lenger kan booke en dag."
              rules={[
                { required: true, message: "Vennligst velg dato for når bookingen stenger" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue("booking_open");
                    if (startDate && value && value.isBefore(startDate)) {
                      return Promise.reject(new Error("Dato for stenging må være senere enn åpningsdatoen."));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker
                showTime={{ defaultValue: moment("12:00:00", "HH:mm:ss"), format: "HH:mm" }}
                showSecond={false}
                showNow={false}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Title level={5}>Sesongens varighet</Title>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="Sesongstart"
              name="semester_start"
              tooltip="Angi første dato som er mulig å booke."
              rules={[
                { required: true, message: "Vennligst velg første dato som er mulig å booke" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const year = getFieldValue("year");
                    const fall = getFieldValue("is_fall");
                    if (year && value && value.year() !== year.year()) {
                      return Promise.reject(new Error("Datoen må være i året du har valgt"));
                    }
                    if (fall !== undefined && fall !== null && fall === true && value && value.month() < 7) {
                      return Promise.reject(new Error("Datoen må være i høstsemesteret"));
                    }
                    if (fall !== undefined && fall !== null && fall === false && value && value.month() > 6) {
                      return Promise.reject(new Error("Datoen må være i vårsemesteret"));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="Sesongslutt"
              name="semester_end"
              tooltip="Angi siste dato som er mulig å booke."
              rules={[
                { required: true, message: "Vennligst velg siste dato som er mulig å booke" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue("semester_start");
                    if (startDate && value && value.isBefore(startDate)) {
                      return Promise.reject(new Error("Sluttdato må være senere enn startdato"));
                    }
                    const year = getFieldValue("year");
                    const fall = getFieldValue("is_fall");
                    if (year && value && value.year() !== year.year()) {
                      return Promise.reject(new Error("Datoen må være i året du har valgt"));
                    }
                    if (fall !== undefined && fall !== null && fall === true && value && value.month() < 7) {
                      return Promise.reject(new Error("Datoen må være i høstsemesteret"));
                    }
                    if (fall !== undefined && fall !== null && fall === false && value && value.month() > 6) {
                      return Promise.reject(new Error("Datoen må være i vårsemesteret"));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Title level={5}>Reserverte og utilgjengelige datoer</Title>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.List name="unavailable_dates_collaboration_partner">
              {(fields, { add, remove }) => (
                // TODO We can consider upgrading antd to 5.14.0 to use <DatePicker multiple />
                <>
                  <Form.Item
                    label="Datoer reservert av SP"
                    required={false}
                    tooltip="Samarbeidspartnere kan reservere dag for bedriftspresentasjon før det offisielle innsalget. Angi disse datoene."
                    style={{ marginBottom: "0px" }}
                  >
                    {fields.map(({ key, name, fieldKey }) => (
                      <div key={key} style={{ width: "100%", marginBottom: "5px" }}>
                        <Form.Item
                          name={name}
                          fieldKey={[fieldKey || 0]}
                          rules={[
                            { required: true, message: "Angi en dato eller slett dette feltet." },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const semesterStart = getFieldValue("semester_start");
                                const semesterEnd = getFieldValue("semester_end");
                                if (semesterStart && value && value.isBefore(semesterStart)) {
                                  return Promise.reject(new Error("Datoen må være etter sesongstart"));
                                }
                                if (semesterEnd && value && value.isAfter(semesterEnd)) {
                                  return Promise.reject(new Error("Datoen må være før sesongslutt"));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          noStyle
                        >
                          <DatePicker />
                        </Form.Item>
                        <Form.Item noStyle>
                          <Button
                            type="text"
                            size="small"
                            title="Fjern dato"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Form.Item>
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add(null)}
                      icon={<PlusOutlined />}
                      style={{ marginBottom: "10px" }}
                    >
                      Legg til ny dato
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.List name="unavailable_dates_other">
              {(fields, { add, remove }) => (
                // TODO We can consider upgrading antd to 5.14.0 to use <DatePicker multiple />
                <>
                  <Form.Item
                    label="Andre utilgjengelige datoer"
                    required={false}
                    tooltip="Angi datoer som av andre grunner ikke kan bookes. Dette kan være pga. UKA, arrangementer av ESTIEM, heligdager, eller lignende."
                    style={{ marginBottom: "0px" }}
                  >
                    {fields.map(({ key, name, fieldKey }) => (
                      <div key={key} style={{ width: "100%", marginBottom: "5px" }}>
                        <Form.Item
                          name={name}
                          fieldKey={[fieldKey || 0]}
                          rules={[
                            { required: true, message: "Angi en dato eller slett dette feltet." },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const semesterStart = getFieldValue("semester_start");
                                const semesterEnd = getFieldValue("semester_end");
                                if (semesterStart && value && value.isBefore(semesterStart)) {
                                  return Promise.reject(new Error("Datoen må være etter sesongstart"));
                                }
                                if (semesterEnd && value && value.isAfter(semesterEnd)) {
                                  return Promise.reject(new Error("Datoen må være før sesongslutt"));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          noStyle
                        >
                          <DatePicker />
                        </Form.Item>
                        <Form.Item noStyle>
                          <Button
                            type="text"
                            size="small"
                            title="Fjern dato"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Form.Item>
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add(null)}
                      icon={<PlusOutlined />}
                      style={{ marginBottom: "10px" }}
                    >
                      Legg til ny dato
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Col>
        </Row>
        {is_fall ? (
          <Row>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label="KD startdato"
                name="unavailable_date_kd_start"
                tooltip="Angi første dag som er utilgjengelig som følge av KarriereDagene. Merk at hele uken normalt sett holdes av."
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const semesterStart = getFieldValue("semester_start");
                      const semesterEnd = getFieldValue("semester_end");
                      if (semesterStart && value && value.isBefore(semesterStart)) {
                        return Promise.reject(new Error("Datoen må være etter sesongstart"));
                      }
                      if (semesterEnd && value && value.isAfter(semesterEnd)) {
                        return Promise.reject(new Error("Datoen må være før sesongslutt"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label="KD sluttdato"
                name="unavailable_date_kd_end"
                tooltip="Angi siste dag som er utilgjengelig som følge av KarriereDagene. Merk at hele uken normalt sett holdes av."
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue("unavailable_date_kd_start");
                      if (startDate && !value) {
                        return Promise.reject(new Error("Vennligst velg sluttdato for KarriereDagene"));
                      }
                      if (startDate && value && value.isBefore(startDate)) {
                        return Promise.reject(new Error("Sluttdato må være senere enn startdato"));
                      }
                      const semesterStart = getFieldValue("semester_start");
                      const semesterEnd = getFieldValue("semester_end");
                      if (semesterStart && value && value.isBefore(semesterStart)) {
                        return Promise.reject(new Error("Datoen må være etter sesongstart"));
                      }
                      if (semesterEnd && value && value.isAfter(semesterEnd)) {
                        return Promise.reject(new Error("Datoen må være før sesongslutt"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker />
              </Form.Item>
            </Col>
          </Row>
        ) : (
          is_fall === false && (
            <Row>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item
                  label="OT startdato"
                  name="unavailable_date_ot_start"
                  tooltip="Angi første dag som er utilgjengelig som følge av OsloTuren."
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const semesterStart = getFieldValue("semester_start");
                        const semesterEnd = getFieldValue("semester_end");
                        if (semesterStart && value && value.isBefore(semesterStart)) {
                          return Promise.reject(new Error("Datoen må være etter sesongstart"));
                        }
                        if (semesterEnd && value && value.isAfter(semesterEnd)) {
                          return Promise.reject(new Error("Datoen må være før sesongslutt"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item
                  label="OT sluttdato"
                  name="unavailable_date_ot_end"
                  tooltip="Angi siste dag som er utilgjengelig som følge av OsloTuren."
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const startDate = getFieldValue("unavailable_date_ot_start");
                        if (startDate && !value) {
                          return Promise.reject(new Error("Vennligst velg sluttdato for OsloTuren"));
                        }
                        if (startDate && value && value.isBefore(startDate)) {
                          return Promise.reject(new Error("Sluttdato må være senere enn startdato"));
                        }
                        const semesterStart = getFieldValue("semester_start");
                        const semesterEnd = getFieldValue("semester_end");
                        if (semesterStart && value && value.isBefore(semesterStart)) {
                          return Promise.reject(new Error("Datoen må være etter sesongstart"));
                        }
                        if (semesterEnd && value && value.isAfter(semesterEnd)) {
                          return Promise.reject(new Error("Datoen må være før sesongslutt"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker />
                </Form.Item>
              </Col>
            </Row>
          )
        )}
        <Row>
          <Col span={24}>
            <Title level={5}>Prismodell</Title>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24}>
            <Form.Item
              label="Prismodell"
              tooltip="Last opp et bilde som viser prisene for de ulike ukene og dagene for bookingsesongen."
            >
              <Upload
                listType="picture-card"
                beforeUpload={file => {
                  setPriceModelImage(file);
                  setRemovePriceModel(false);
                  return false;
                }}
                onRemove={() => {
                  setPriceModelImage(null);
                  setPriceModelImagePreview(null);
                  setRemovePriceModel(true);
                }}
                fileList={
                  removePriceModel
                    ? []
                    : priceModelImagePreview
                    ? [
                        {
                          uid: "-1",
                          name: "Prismodell",
                          status: "done",
                          url: priceModelImagePreview,
                        },
                      ]
                    : season?.price_model
                    ? [
                        {
                          uid: "-1",
                          name: season.price_model?.substring(season.price_model?.lastIndexOf("/") + 1) || "Bilde",
                          status: "done",
                          url: axios.defaults.baseURL + season.price_model,
                        },
                      ]
                    : []
                }
              >
                {uploadButton}
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Title level={5}>Forhåndsvisning og opprettelse</Title>
          </Col>
          <Col span={24}>
            <Button type="dashed" onClick={handlePreview} disabled={fetching} style={{ width: "100%" }}>
              {preview ? "Skjul forhåndsvisning og fortsett å redigere" : "Forhåndsvis bookingkalender"}
            </Button>
          </Col>
          <Col span={24}>
            {preview && (
              <Row justify="center">
                <Calendar
                  bookingSeason={preview}
                  setSelectedDay={day => {
                    return day;
                  }}
                  loading={false}
                />
              </Row>
            )}
          </Col>
          {preview && (
            <Col span={24}>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!preview}
                  loading={loading}
                  style={{ width: "100%" }}
                >
                  {id ? "Lagre endringer" : "Opprett bookingsesongen"}
                </Button>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Card>
  );
};

export default CreateOrEditBookingSeason;
